import { useState, useEffect, useRef } from 'react';

/**
 * Hook to manage completion modal data snapshot.
 * Creates a stable snapshot when puzzle is solved that won't change
 * even if game state resets, preventing modal flicker on mobile.
 * 
 * @param {Object} params - Parameters for completion tracking
 * @param {boolean} params.isComplete - Whether the puzzle is complete
 * @param {Object} params.scoreData - Score calculation data
 * @param {number} params.time - Time taken
 * @param {number} params.moves - Number of moves
 * @param {string} params.difficulty - Difficulty level
 * @param {string} params.imageUrl - Current puzzle image URL
 * @param {boolean} params.isDailyMode - Whether in daily mode
 * @param {boolean} params.isChallengeMode - Whether in challenge mode
 * @param {string} params.challengeCode - Current challenge code
 * @param {string} params.dailyPuzzleDate - Daily puzzle date
 * @param {number} params.gridSize - Grid size
 * @param {number} params.hintPenalty - Points lost due to hints
 */
export function useCompletionModal({
  isComplete,
  scoreData,
  time,
  moves,
  difficulty,
  imageUrl,
  isDailyMode,
  isChallengeMode,
  challengeCode,
  dailyPuzzleDate,
  gridSize,
  hintPenalty = 0
}) {
  // Stable completion modal state
  const [completionModalData, setCompletionModalData] = useState(null);
  // Track the last captured imageUrl to prevent re-capturing for same puzzle
  const [lastCapturedImageUrl, setLastCapturedImageUrl] = useState(null);
  // Track if modal was dismissed by user action (prevents re-showing on new puzzle)
  const dismissedRef = useRef(false);

  // Capture completion data when puzzle is solved
  useEffect(() => {
    // Only capture if:
    // 1. Game is complete with valid score
    // 2. We don't already have modal data
    // 3. This is a NEW completion (different image than last captured)
    // 4. Modal wasn't just dismissed by user (prevents race condition on New Puzzle)
    if (isComplete && scoreData && !completionModalData && imageUrl !== lastCapturedImageUrl && !dismissedRef.current) {
      setCompletionModalData({
        scoreData,
        time,
        moves,
        difficulty,
        imageUrl,
        isDailyMode,
        isChallengeMode,
        challengeCode,
        dailyPuzzleDate,
        gridSize,
        hintPenalty
      });
      setLastCapturedImageUrl(imageUrl);
    }
    
    // Reset dismissed flag when isComplete becomes false (new game started)
    if (!isComplete) {
      dismissedRef.current = false;
    }
  }, [
    isComplete, 
    scoreData, 
    time, 
    moves, 
    difficulty, 
    imageUrl, 
    isDailyMode, 
    isChallengeMode, 
    challengeCode, 
    dailyPuzzleDate, 
    gridSize, 
    hintPenalty,
    completionModalData, 
    lastCapturedImageUrl
  ]);

  // Clear completion modal data
  const clearCompletionModal = () => {
    // Mark as dismissed to prevent re-showing during New Puzzle transition
    dismissedRef.current = true;
    setCompletionModalData(null);
    // Note: We keep lastCapturedImageUrl so we don't re-capture during the transition
    // It will be different for the new puzzle anyway
  };
  
  // Clear for replay (same puzzle) - need to clear lastCapturedImageUrl
  const clearForReplay = () => {
    dismissedRef.current = false;
    setCompletionModalData(null);
    setLastCapturedImageUrl(null);
  };

  // Check which type of completion modal should be shown
  const getCompletionModalType = () => {
    if (!completionModalData) return null;
    if (completionModalData.isDailyMode) return 'daily';
    if (completionModalData.isChallengeMode) return 'challenge';
    return 'regular';
  };

  return {
    completionModalData,
    clearCompletionModal,
    clearForReplay,
    getCompletionModalType,
    isCompletionModalOpen: !!completionModalData
  };
}
